'use client';

import { useEffect, useState } from 'react';

/**
 * Display modes an installed copy of the site runs in. The manifest asked for
 * 'standalone' until 2026-10-04, and a browser that cannot do standalone falls
 * back to 'minimal-ui'. 'fullscreen' is left out because Chrome also reports it
 * for an ordinary tab after F11, which would show this to visitors who never
 * installed anything.
 */
const INSTALLED_DISPLAY_QUERY = '(display-mode: standalone), (display-mode: minimal-ui)';

/**
 * Asks a visitor who installed supa.place as an app, before the manifest moved
 * to `display: 'browser'` (src/app/manifest.ts), to remove it and use the
 * website instead. The manifest change stops new installs, but an existing one
 * can keep its standalone window: iOS fixes the mode when the icon is added,
 * and a desktop install keeps the user's own "open as window" choice. No web
 * API can uninstall an app or reliably move a page out of its window, so the
 * notice says what to do rather than offering a button that may not work.
 *
 * Renders nothing on the server and on the first client render, because the
 * display mode is only known in the browser; the bar appears after mount, and
 * only in an installed window. `navigator.standalone` is iOS's older signal for
 * a home-screen web app.
 */
export function InstalledAppNotice() {
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
    setInstalled(iosStandalone || window.matchMedia(INSTALLED_DISPLAY_QUERY).matches);
  }, []);

  if (!installed) return null;

  return (
    <div className="border-b border-border bg-muted px-4 py-2 text-center text-sm text-foreground">
      supa.place no longer runs as an installed app. Please remove it from your home screen or apps,
      then open supa.place in your web browser.
    </div>
  );
}
